<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import helperService from "@/services/helper";
import {
  getMedicalInsuranceName,
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
} from "@/services/patient-utils";
import PatientList from "@/components/patient/PatientList.vue";
import AppointmentSummary from "@/components/appointment/AppointmentSummary.vue";
import IndicationForm from "@/components/appointment/indications/IndicationForm.vue";
import patientService from "@/services/patient";
import pagination from "@/mixins/pagination";
import { get } from "lodash";
import {
  isIndicationsEnabled,
  arePrescriptionNotesAndCertificatesEnabled,
  isManualPatientCreationRestricted,
} from "@/services/config";
import { mapGetters } from "vuex";
import { cloneDeep, debounce } from "lodash";
import { INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import { filterPersistibleIndications } from "@/services/indication-filter";
import formService from "@/services/form";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "Prescription",
  mixins: [pagination],
  components: {
    BaseSubNav,
    PatientList,
    AppointmentSummary,
    IndicationForm,
  },
  data() {
    return {
      searchInput: "",
      patients: [],
      patientSelected: null,
      isSavingIndications: false,
      savedIndications: [],
      completedForms: [],
      isIndicationsEnabled,
      appointmentStatus: "appointment",
      forms: cloneDeep(INDICATIONS_TYPES),
      showPatientTable: true,
    };
  },
  computed: {
    ...mapGetters(["isDoctor", "isAdmin", "isAssistant", "isManager"]),

    canCreatePatient() {
      if (!isManualPatientCreationRestricted) return true;
      return (
        !this.isAdmin && !this.isDoctor && !this.isAssistant && !this.isManager
      );
    },

    enabledIndicationTypes() {
      const baseTypes = ["medications", "images", "lab", "practices"];
      if (arePrescriptionNotesAndCertificatesEnabled && this.isDoctor) {
        return [...baseTypes, "notes", "certificates"];
      }
      return baseTypes;
    },

    patientIndicationsFilter() {
      return this.enabledIndicationTypes;
    },

    prescriptionForms() {
      return this.forms.filter((form) =>
        this.enabledIndicationTypes.includes(form.type)
      );
    },
  },
  created() {
    const newlyCreatedPatient = localStorage.getItem("newlyCreatedPatient");
    if (newlyCreatedPatient) {
      try {
        const patient = JSON.parse(newlyCreatedPatient);

        localStorage.removeItem("newlyCreatedPatient");

        this.patientSelected = patient;
        this.patients = [patient];
        this.searchInput = "";

        this.$message.success(
          `Paciente ${this.getFullName(patient)} creado correctamente`
        );
        this.$message.success(
          `Paciente ${this.getFullName(patient)} seleccionado automáticamente`
        );
      } catch (error) {
        console.error("Error al procesar paciente del localStorage:", error);
        localStorage.removeItem("newlyCreatedPatient");
      }
    }

    this.$bus.$on("updateIndication", (indication, isDeleted = false) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indication.key
      );
      if (indicationIndex !== -1) {
        if (!isDeleted) {
          this.$set(this.savedIndications, indicationIndex, indication);
        } else {
          this.savedIndications.splice(indicationIndex, 1);
        }
      } else {
        if (!isDeleted) {
          this.savedIndications.push(indication);
        }
      }
    });

    this.$bus.$on("filesUploaded", (indicationKey) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indicationKey
      );
      if (indicationIndex !== -1) {
        this.$set(this.savedIndications, indicationIndex, {
          ...this.savedIndications[indicationIndex],
          hasFiles: false,
        });
      }

      if (this.savedIndications.every((indication) => !indication.hasFiles)) {
        this.isSavingIndications = false;
        this.$router.push({ name: "home" });
      }
    });

    this.$bus.$on("form-completed", this.updateCompletedForms);
    this.$bus.$on("form-updated", this.updateCompletedForms);
  },

  beforeDestroy() {
    this.$bus.$off("form-completed", this.updateCompletedForms);
    this.$bus.$off("form-updated", this.updateCompletedForms);
  },

  methods: {
    updateCompletedForms() {
      const indicationFormRef = this.$refs.indicationForm;
      if (!indicationFormRef) return;

      const allForms = [];
      const allIndicationRefs = indicationFormRef.$refs;

      for (const refName of Object.keys(allIndicationRefs)) {
        if (refName.startsWith("indications-")) {
          const indicationComponent = allIndicationRefs[refName];
          if (indicationComponent?.[0]?.completedForms) {
            const completedForms = indicationComponent[0].completedForms;
            for (const indicationKey of Object.keys(completedForms)) {
              allForms.push(...completedForms[indicationKey]);
            }
          }
        }
      }
      this.completedForms = allForms;
    },
    searchPatient() {
      if (!this.searchInput.trim()) {
        this.patients = [];
        this.pagination.total = 0;
        return;
      }

      this.showPatientTable = true;

      const query = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
      };

      const input = this.searchInput && this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);

      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useSearchIndex) {
        query.$text = {
          $search: this.searchInput,
        };
      } else if (useNameIndex) {
        Object.assign(query, helperService.createNameQuery(this.searchInput));
      }

      this.getPatients(query);
    },
    debounceInput: debounce(function () {
      this.searchPatient();
    }, DEBOUNCE_MS_TIMEOUT),
    getFullName(patient) {
      return helperService.getFullName(patient);
    },
    getMedicalInsuranceName,
    getMedicalInsurancePlan,
    getMedicalInsuranceNumber,
    get,
    getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      defaultQuery.doctors = this.doctorFilter || undefined;

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    selectPatient(patientId) {
      this.patientSelected = this.patients.find(
        (patient) => patient._id === patientId
      );
      this.showPatientTable = false;
      this.searchInput = "";
    },
    isHighlighted(value) {
      if (value === this.appointmentStatus) {
        return "selected";
      } else {
        return "not-selected";
      }
    },
    canSaveIndications() {
      const hasIndications =
        this.savedIndications.length > 0 &&
        this.savedIndications.some(
          (indication) =>
            !indication.createdAt &&
            this.patientIndicationsFilter.includes(indication.type)
        );

      const hasCompletedForms = this.completedForms.length > 0;

      return !this.isSavingIndications && (hasIndications || hasCompletedForms);
    },

    getAllCompletedForms() {
      return this.completedForms;
    },
    triggerUpload(createdIndication) {
      this.$refs.indicationForm.triggerUpload(createdIndication);
    },
    async saveIndications(typeFilter = null) {
      // La bandera se levanta acá, de forma sincrónica, antes de cualquier
      // await. Si se deja que la ponga el servicio, un segundo clic entra
      // mientras la primera llamada sigue en vuelo y duplica la orden.
      if (this.isSavingIndications) {
        return;
      }
      this.isSavingIndications = true;

      try {
        const validIndications = filterPersistibleIndications(
          this.savedIndications,
          typeFilter
        );
        await indicationService.saveIndications(
          validIndications,
          this.patientSelected._id,
          undefined, // No appointmentId en Prescription
          typeFilter,
          this.$store,
          {
            onTriggerUpload: this.triggerUpload.bind(this),
            onUpdateIndication: (updatedIndication) => {
              const index = this.savedIndications.findIndex(
                (ind) => ind.key === updatedIndication.key
              );
              if (index !== -1) {
                this.$set(this.savedIndications, index, updatedIndication);
              }
            },
          }
        );

        await this.sendCompletedForms();

        const hasPendingFiles = this.savedIndications.some(
          (indication) => indication.hasFiles
        );

        if (!hasPendingFiles) {
          this.$message.success("Prescripciones enviadas correctamente");
          this.$router.push({ name: "home" });
        }
      } catch (error) {
        console.log(error);
      } finally {
        // Si quedaron archivos subiéndose, el guardado sigue abierto y lo
        // libera el evento "filesUploaded".
        if (this.savedIndications.every((indication) => !indication.hasFiles)) {
          this.isSavingIndications = false;
        }
      }
    },

    async sendCompletedForms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];
            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;

              for (const indicationKey of Object.keys(completedForms)) {
                const formsToSend = completedForms[indicationKey];

                for (const completedForm of formsToSend) {
                  if (!completedForm.isSent) {
                    await this.sendFormToBackend(
                      completedForm,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al enviar formularios completados:", error);
      }
    },

    async sendFormToBackend(completedForm, indicationKey, indicationComponent) {
      try {
        const formData = {
          form: completedForm.form?._id || completedForm._id,
          patient: this.patientSelected._id,
          sections: completedForm.sections || [],
          answeredAt: completedForm.answeredAt || new Date(),
          status: completedForm.status || "open",
          createdBy: this.$store.state.user,
        };

        const createdForm = await formService.postPatientForm(formData);

        completedForm.isSent = true;
        completedForm._id = createdForm._id;
        completedForm.sentAt = new Date();

        if (completedForm.hasFiles && completedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(
            createdForm._id,
            completedForm.dropzoneFiles
          );
        }

        this.updateIndication(indicationComponent);
      } catch (error) {
        console.error("Error al enviar formulario al backend:", error);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    updateIndication(indication) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), false);
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav
    template(#title)
      | Prescripciones 
    template(#actions)
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        placeholder="Buscar"
        :clearable="true"
        @input="debounceInput"
      )
      button.button.button--blue(@click="searchPatient")
        micon(name="search").button__icon
        span.button__text Buscar
      router-link.button.button--blue(
        v-if="canCreatePatient"
        v-auth="'patient.create'"
        :to="{name: 'create-patient'}"
      )
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Paciente
  .box.box--with-subnav(v-show="showPatientTable")
    .box__content--stretch(style="max-height: 400px; overflow-y: auto;")
      table
        thead
          tr
            th Paciente
            th Seguro Salud
            th Plan
            th Nro Afiliado
            th Documento
            th Edad
            th Última Atención
        tbody
          tr(v-if="patients.length === 0")
            td(colspan="7" style="text-align: center; padding: 40px; color: #909399;")
              | No hay resultados
          tr.clickable(v-else v-for="patient in patients" :key="patient._id" @click="selectPatient(patient._id)")
            td {{ getFullName(patient) }}
            td {{ getMedicalInsuranceName(patient) }}
            td {{ getMedicalInsurancePlan(patient) }}
            td {{ getMedicalInsuranceNumber(patient) }}
            td {{ patient.governmentId?.number || 'N/A' }}
            td {{ patient.bornAt | age }}
            td {{ patient.patient?.lastEvent?.createdAt | formatDate }}
  div.prescription-container(v-show="patientSelected")
    .whiteBox
      .patient-header-container.top
        .patient-header-top
          p.secondary-text Prescribiendo a
          h1 {{ getFullName(patientSelected) }}
        .patient-header-top
          button.button-blue-new(
            @click="saveIndications(patientIndicationsFilter)"
            :disabled="!canSaveIndications || isSavingIndications"
          ) Enviar prescripciones
      .patient-header-container
        .patient-header-bottom
          p.secondary-text Edad
          p.primary-text {{ patientSelected?.bornAt | age }}
        .patient-header-bottom
          p.secondary-text DNI
          p.primary-text {{ get(patientSelected, 'governmentId.number', 'N/A') }}
        .patient-header-bottom
          p.secondary-text Teléfono
          p.primary-text {{ patientSelected?.phone }}
        .patient-header-bottom
          p.secondary-text E-mail
          p.primary-text {{ patientSelected?.email }}
        .patient-header-bottom
          p.secondary-text Seguro Salud
          p.primary-text {{ getMedicalInsuranceName(patientSelected) }}
        .patient-header-bottom
          p.secondary-text Cobertura
          p.primary-text {{ getMedicalInsurancePlan(patientSelected) }}
        .patient-header-bottom
          p.secondary-text # de socio
          p.primary-text {{ getMedicalInsuranceNumber(patientSelected) }}
    .patient-attention-container
      .patient-attention-left
        .patient-attention-header
          p(:class="isHighlighted('appointment')") Prescripción

        indication-form(
          v-show="isIndicationsEnabled && appointmentStatus === 'appointment'"
          ref="indicationForm"  
          :forms="prescriptionForms"
          :savedIndications="savedIndications"
        )

      .patient-attention-right
          AppointmentSummary( 
            :savedIndications="savedIndications" 
            :context="'prescription'"
            :completedForms="getAllCompletedForms"
          )
</template>

<style lang="scss">
.prescription-container {
  margin-top: 20px;
}

.patient-attention-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row;

  .patient-attention-right {
    width: 500px;
    margin-left: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 500px;
    overflow: hidden;
  }

  .patient-attention-left {
    width: 100%;
    padding: 15px 25px;
    margin-bottom: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 600px;

    .patient-attention-header {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 30px;

      .selected {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $steel-blue;
        font-weight: 600;
        border-bottom: solid 2px $steel-blue;
        width: 100%;
        padding-bottom: 10px;
        margin-right: 2px;
      }

      .not-selected {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $primary;
        font-weight: 600;
        border-bottom: solid 2px rgba(0, 0, 0, 0.153);
        width: 100%;
        padding-bottom: 10px;
        margin-left: 2px;
      }
    }
  }
}

.clickable {
  cursor: pointer;
}

.whiteBox {
  width: 100%;
  padding: 15px 25px;
  margin-bottom: 20px;
  background-color: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);

  .secondary-text {
    font-size: 13px;
    margin: 7px 0;
  }

  .primary-text {
    color: $primary;
    font-weight: 600;
    margin: 7px 0;
  }

  .patient-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .patient-header-bottom {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }
  }

  .patient-header-container.top {
    padding-bottom: 15px;
    margin-bottom: 10px;
    border-bottom: solid 1px #00000024;

    .patient-header-top {
      display: flex;
      justify-content: center;
      align-items: center;

      h1 {
        margin: 0 15px;
      }
    }
  }
}
</style>
